import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HTTP_STATUS } from 'src/app/@core/auth/constants/auth-constants';
import { AuthSessionService } from 'src/app/@core/auth/services/auth-session';
import { AuthTokenService } from 'src/app/@core/auth/services/auth-token';
import { AuthStore } from 'src/app/@core/auth/stores/auth-store';
import { AuthStateService } from 'src/app/@store/auth-state.service';
import { ExternalRegistrationStateService } from '../@store/external-registration-state.service';
import { environment } from 'src/environments/environment';
import { ENROLLED_USER_SESSION } from '../@constants/local-storage-key-constants';
import { EventEnrollUserSessionResponse } from '../@data-services/event-enroll/event-enroll.data-model';
import { getLocalStorageItem } from '../@utils/local-storage-util';

const PUBLIC_API_PATTERNS: string[] = ['/auth/', '/public/', '/getEventConfig'];

/** True when the request targets the show-winners networking backend. */
function isShowWinnersUrl(url: string): boolean {
  if (/^\/api(\/|$|\?)/i.test(url)) return true;
  const base = environment.matchmakingApiUrl;
  return !!base && url.startsWith(base);
}

function requiresCognitoAuth(url: string): boolean {
  return !PUBLIC_API_PATTERNS.some((pattern) => url.includes(pattern));
}

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  if (isShowWinnersUrl(req.url)) {
    return next(req);
  }

  const authStateService = inject(AuthStateService);
  const externalRegistrationState = inject(ExternalRegistrationStateService);
  const tokenService = inject(AuthTokenService);
  const sessionService = inject(AuthSessionService);
  const authStore = inject(AuthStore);

  const authUserId = authStateService.$userId();
  const authRegistrationId = authStateService.$registrationId();
  const externalRegistrationId = externalRegistrationState.$registrationId();
  const registrationId = authRegistrationId || externalRegistrationId;
  const userId = authUserId || '';

  const session = getLocalStorageItem<EventEnrollUserSessionResponse>(
    ENROLLED_USER_SESSION
  );
  const userSessionId = session?.sessionID || '';

  let reqWithHeaders = req.clone({
    headers: req.headers
      .set('X-Api-Key', environment.X_API_KEY)
      .set('X-User-Session', userSessionId),
  });

  if (req.body && typeof req.body === 'object') {
    const bodyWithAuth: Record<string, unknown> = {
      ...req.body,
      registrationId,
    };
    if (userId) {
      bodyWithAuth['userId'] = userId;
    }
    reqWithHeaders = reqWithHeaders.clone({
      body: bodyWithAuth,
    });
  }

  const isAuthenticated = authStore.$isAuthenticated();
  const needsCognitoAuth = requiresCognitoAuth(req.url);

  if (!needsCognitoAuth || !isAuthenticated) {
    return next(reqWithHeaders);
  }

  return tokenService.getValidToken$().pipe(
    switchMap((token) => {
      const authorizedReq = reqWithHeaders.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
      return next(authorizedReq);
    }),
    catchError((error: HttpErrorResponse) => {
      if (authStore.$isLoggingOut()) {
        return throwError(() => error);
      }

      if (error.status === HTTP_STATUS.UNAUTHORIZED) {
        return tokenService.forceRefresh$().pipe(
          switchMap((newToken) => {
            const retryReq = reqWithHeaders.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            return next(retryReq);
          }),
          catchError((retryError) =>
            sessionService
              .logout$()
              .pipe(switchMap(() => throwError(() => retryError)))
          )
        );
      }

      return throwError(() => error);
    })
  );
}
